import { Box, Button, Chip, IconButton, LinearProgress, Link, Stack, Typography, useTheme } from '@mui/material'
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ErrorIcon from '@mui/icons-material/Error'
import SensorsIcon from '@mui/icons-material/Sensors'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import MapIcon from '@mui/icons-material/Map'
import ShareIcon from '@mui/icons-material/Share'
import fontColorContrast from 'font-color-contrast'
import { useNavigate } from '@tanstack/react-router'
import { useLocalStorage } from '@uidotdev/usehooks'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { Leg, Step } from 'src/generated/brezavta/requests/types.gen'
import { getColorForMode, getForegroundColorForMode, getIconForMode } from 'src/lib/modes'
import { FormatType, formatDuration, formatNavTime } from 'src/lib/util'
import { TRANSIT_MODES } from 'src/lib/navigationUtils'
import { getDirectionIcon, getDirectionLabel, isSignificantDirection, formatStepDistance } from 'src/lib/directions'
import { Direction } from 'src/generated/brezavta/requests/types.gen'

interface NextStopInfo {
    name: string
    gtfs_id: string
}

interface TransferOption {
    tripId: string
    routeName: string
    headsign: string
    departureTime: number
    bufferMs: number
    gtfsId: string
}

interface CurrentTransfer {
    stopId: string
    stopName: string
    plannedDepartureTime: number
    plannedBufferMs: number
    alternative?: TransferOption
}

interface Props {
    leg: Leg
    nextLeg?: Leg
    legIndex: number
    totalLegs: number
    planStartTime: number
    planEndTime: number
    nextStop?: NextStopInfo | null
    transferStatus?: 'ok' | 'at-risk' | 'missed'
    delaySeconds?: number
    currentTransfer?: CurrentTransfer
    nextWalkStep?: Step | null
    onSwitchToAlternative?: (alternative: TransferOption) => void
    onStop: () => void
    onReplan: () => void
    onViewPlan: () => void
    onShare?: () => void
    onPrevStep?: () => void
    onNextStep?: () => void
}

export default function NavigatorLegCard({ leg, nextLeg, legIndex, totalLegs, planStartTime, planEndTime, nextStop, transferStatus, delaySeconds, currentTransfer, nextWalkStep, onSwitchToAlternative, onStop, onReplan, onViewPlan, onShare, onPrevStep, onNextStep }: Props) {
    const { t } = useTranslation()
    const theme = useTheme()
    const [timeFormat] = useLocalStorage<FormatType>('timeFormat', 'relative')
    const [now, setNow] = useState(() => Date.now())

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000)
        return () => clearInterval(timer)
    }, [])

    const isLast = legIndex === totalLegs - 1
    const isTransit = TRANSIT_MODES.includes(leg.mode)
    const isArrived = isLast && now > leg.end_time

    return (
        <Box sx={{ background: `color-mix(in srgb, ${theme.palette.background.paper} 90%, transparent)`, backdropFilter: 'blur(24px) saturate(1.6)', WebkitBackdropFilter: 'blur(24px) saturate(1.6)', boxShadow: '0 -4px 32px rgba(0,0,0,0.1)', overflow: 'hidden', borderRadius: '24px 24px 0 0' }}>
            <LinearProgress variant="determinate" value={Math.min(100, Math.max(0, (now - planStartTime) / (planEndTime - planStartTime) * 100))} sx={{ height: 3 }} />

            <Stack sx={{ p: 2, gap: 1.5 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                    <Stack gap={1} flex={1} minWidth={0}>
                        {isArrived
                            ? <ArrivedCard leg={leg} />
                            : isTransit
                                ? <TransitCard leg={leg} timeFormat={timeFormat} nextStop={nextStop} transferStatus={transferStatus} delaySeconds={delaySeconds} onReplan={onReplan} />
                                : leg.mode === 'WALK'
                                    ? <WalkCard leg={leg} nextLeg={nextLeg} nextWalkStep={nextWalkStep} />
                                    : <SelfPropelledCard leg={leg} />}
                    </Stack>

                    <Stack alignItems="flex-end" gap={0.5} ml={1} flexShrink={0}>
                        <IconButton size="small" onClick={onViewPlan} aria-label={t('View plan')}>
                            <MapIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={onShare} disabled={!onShare} aria-label={t('Share trip')}>
                            <ShareIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </Stack>

                {/* Better alternative — shown before action buttons so it's never below the fold */}
                {currentTransfer?.alternative && (
                    <Box sx={{ bgcolor: 'info.light', p: 1.5, borderRadius: 1 }}>
                        <Typography variant="caption" fontWeight={600} color="info.contrastText">
                            {t('Better option available')}
                        </Typography>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                            <Box>
                                <Typography variant="body2" color="info.contrastText" fontWeight={500}>
                                    {currentTransfer.alternative.routeName}
                                    {' '}
                                    →
                                    {currentTransfer.alternative.headsign}
                                </Typography>
                                <Typography variant="caption" color="info.contrastText" sx={{ opacity: 0.8 }}>
                                    {t('Departs')}
                                    {' '}
                                    {formatNavTime(currentTransfer.alternative.departureTime, timeFormat)}
                                    {' · '}
                                    {Math.round((currentTransfer.alternative.bufferMs - currentTransfer.plannedBufferMs) / 60000)}
                                    {' '}
                                    {t('min faster')}
                                </Typography>
                            </Box>
                            <Chip
                                label={t('Switch')}
                                size="small"
                                onClick={() => onSwitchToAlternative?.(currentTransfer.alternative!)}
                                sx={{ bgcolor: 'white', color: 'info.main' }}
                            />
                        </Stack>
                    </Box>
                )}

                {isArrived
                    ? (
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={onStop}
                                sx={{ borderRadius: 2, py: 1.25, fontWeight: 700 }}
                            >
                                {t('End navigation')}
                            </Button>
                        )
                    : (
                            <>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        onClick={onStop}
                                        sx={{ borderRadius: 2, fontSize: '0.72rem', py: 0.5, px: 1.25 }}
                                    >
                                        {t('End navigation')}
                                    </Button>
                                    <Stack direction="row" gap={0.5}>
                                        <IconButton size="small" onClick={onPrevStep} disabled={legIndex === 0}>
                                            <ArrowBackIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                        <IconButton size="small" onClick={onNextStep} disabled={legIndex === totalLegs - 1}>
                                            <ArrowForwardIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Stack>
                                </Stack>
                                {nextLeg && <NextStepPreview nextLeg={nextLeg} />}
                            </>
                        )}
            </Stack>
        </Box>
    )
}

function TransferBanner({ transferStatus, onReplan }: { transferStatus?: 'ok' | 'at-risk' | 'missed', onReplan: () => void }) {
    const { t } = useTranslation()
    if (!transferStatus || transferStatus === 'ok') return null
    const isMissed = transferStatus === 'missed'
    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
                px: 1.5, py: 0.75, borderRadius: 1,
                bgcolor: isMissed ? 'error.light' : 'warning.light',
                color: isMissed ? 'error.contrastText' : 'warning.contrastText',
            }}
        >
            <Stack direction="row" alignItems="center" gap={0.5}>
                {isMissed
                    ? <ErrorIcon sx={{ fontSize: 16 }} />
                    : <WarningAmberIcon sx={{ fontSize: 16 }} />}
                <Typography variant="caption" fontWeight={600}>
                    {isMissed ? t('Transfer missed') : t('Transfer at risk')}
                </Typography>
            </Stack>
            <Chip
                label={t('Re-plan')}
                size="small"
                onClick={onReplan}
                sx={{ cursor: 'pointer', fontSize: '0.7rem', height: 22 }}
            />
        </Stack>
    )
}

function delayBadge(delaySeconds?: number): { text: string, color: string } | null {
    if (!delaySeconds || Math.abs(delaySeconds) < 60) return null
    const mins = Math.round(Math.abs(delaySeconds) / 60)
    return delaySeconds > 0
        ? { text: `+${mins}m`, color: 'error.main' }
        : { text: `-${mins}m`, color: 'warning.main' }
}

function TransitCard({ leg, timeFormat, nextStop, transferStatus, delaySeconds, onReplan }: {
    leg: Leg
    timeFormat: FormatType
    nextStop?: NextStopInfo | null
    transferStatus?: 'ok' | 'at-risk' | 'missed'
    delaySeconds?: number
    onReplan: () => void
}) {
    const { t } = useTranslation()
    const bgColor = getColorForMode(leg, 'pill') ?? '#000000'
    const textColor = getForegroundColorForMode(leg) || fontColorContrast(bgColor)
    const icon = getIconForMode(leg)
    const badge = delayBadge(delaySeconds)

    const nextIdx = nextStop && leg.intermediate_stops
        ? leg.intermediate_stops.findIndex(s => s.gtfs_id === nextStop.gtfs_id)
        : -1
    const stopsLeft = nextIdx >= 0 ? leg.intermediate_stops!.length - nextIdx : 0

    return (
        <>
            {/* Route pill + headsign */}
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                <Chip
                    label={(
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            {icon}
                            {leg.trip?.short_name}
                        </Stack>
                    )}
                    size="small"
                    sx={{ bgcolor: bgColor, color: textColor, fontWeight: 700 }}
                />
                <Typography variant="body1" fontWeight={600} noWrap>{leg.trip?.headsign}</Typography>
                {leg.realtime && <SensorsIcon sx={{ color: 'success.main', fontSize: 16, animation: 'blink 2s linear infinite' }} />}
            </Stack>

            {/* Board at */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Stack minWidth={0} flex={1} mr={2}>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>{t('Board at')}</Typography>
                    <Typography variant="body2" fontWeight={500} noWrap>{leg.from_point.name}</Typography>
                </Stack>
                <Stack alignItems="flex-end" flexShrink={0}>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        <Typography variant="body1" fontWeight={700}>{formatNavTime(leg.from_point.departure_time + (delaySeconds ?? 0) * 1000, timeFormat)}</Typography>
                        {badge && <Typography variant="caption" sx={{ color: badge.color, fontWeight: 700 }}>{badge.text}</Typography>}
                    </Stack>
                    <Typography variant="caption" color={leg.realtime ? 'success.main' : 'text.secondary'}>
                        {leg.realtime_status ?? t('Scheduled')}
                    </Typography>
                </Stack>
            </Stack>

            {/* Next stop */}
            {nextStop && (() => {
                const fullStop = leg.intermediate_stops?.find(s => s.gtfs_id === nextStop.gtfs_id)
                return (
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Stack minWidth={0} flex={1} mr={2}>
                            <Typography variant="caption" color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
                                {`${t('Next stop')}${stopsLeft > 1 ? ` · ${stopsLeft} ${t('remaining')}` : ` · ${t('last before exit')}`}`}
                            </Typography>
                            <Typography variant="body2" noWrap>{nextStop.name}</Typography>
                        </Stack>
                        {fullStop?.departure_time && (
                            <Typography variant="body2" color="text.secondary" flexShrink={0}>
                                {formatNavTime(fullStop.departure_time + (delaySeconds ?? 0) * 1000, timeFormat)}
                            </Typography>
                        )}
                    </Stack>
                )
            })()}

            {/* Alight at */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Stack minWidth={0} flex={1} mr={2}>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>{t('Alight at')}</Typography>
                    <Typography variant="body2" fontWeight={500} noWrap>{leg.to_point.name}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={0.5} flexShrink={0}>
                    <Typography variant="body1" fontWeight={700}>
                        {formatNavTime(leg.to_point.arrival_time + (delaySeconds ?? 0) * 1000, timeFormat)}
                    </Typography>
                    {badge && <Typography variant="caption" sx={{ color: badge.color, fontWeight: 700 }}>{badge.text}</Typography>}
                </Stack>
            </Stack>

            <TransferBanner transferStatus={transferStatus} onReplan={onReplan} />
        </>
    )
}

function WalkCard({ leg, nextLeg, nextWalkStep }: { leg: Leg, nextLeg?: Leg, nextWalkStep?: Step | null }) {
    const { t } = useTranslation()
    const steps = leg.steps ?? []
    const distM = steps.reduce((s, step) => s + step.distance, 0)
    const distStr = distM < 1000 ? `${distM.toFixed(0)} m` : `${(distM / 1000).toFixed(1)} km`

    // Use position-aware next step if available, fall back to first significant step
    const effectiveNextStep = nextWalkStep
        ?? steps.find(step => step.relative_direction && isSignificantDirection(step.relative_direction as Direction))
    const nextTurnDirection = effectiveNextStep?.relative_direction as Direction | undefined
    const nextTurnStreet = effectiveNextStep?.street_name ?? ''
    const nextTurnDistance = effectiveNextStep?.distance ?? 0

    return (
        <>
            <Stack direction="row" alignItems="center" gap={1}>
                <DirectionsWalkIcon sx={{ color: 'text.secondary' }} />
                <Typography variant="body1" fontWeight={600}>{t('Walk')}</Typography>
                <Typography variant="body2" color="text.secondary">{`${distStr} · ${formatDuration(leg.end_time - leg.start_time)}`}</Typography>
            </Stack>
            {nextTurnDirection && (
                <Stack direction="row" alignItems="center" gap={0.5} sx={{ color: 'text.secondary' }}>
                    <Typography variant="body2" fontWeight={600}>
                        {getDirectionIcon(nextTurnDirection)}
                        {' '}
                        {getDirectionLabel(nextTurnDirection, t)}
                    </Typography>
                    <Typography variant="body2">
                        {t('in')}
                        {' '}
                        {formatStepDistance(nextTurnDistance)}
                        {' '}
                        {t('on')}
                        {' '}
                        {nextTurnStreet}
                    </Typography>
                </Stack>
            )}
            {leg.to_point.name && (
                <Stack direction="row" alignItems="center" gap={0.5}>
                    <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary" noWrap>{leg.to_point.name}</Typography>
                </Stack>
            )}
            {nextLeg && TRANSIT_MODES.includes(nextLeg.mode) && (
                <Typography variant="caption" color="primary.main">
                    {`${t('Then board')} ${nextLeg.trip?.short_name ?? ''} → ${nextLeg.trip?.headsign ?? ''}`}
                </Typography>
            )}
        </>
    )
}

function SelfPropelledCard({ leg }: { leg: Leg }) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const steps = leg.steps ?? []
    const distM = steps.reduce((s, step) => s + step.distance, 0)
    const distStr = distM < 1000 ? `${distM.toFixed(0)} m` : `${(distM / 1000).toFixed(1)} km`
    const icon = getIconForMode(leg)
    const verb = leg.mode === 'BICYCLE' ? t('Cycle') : leg.mode === 'SCOOTER' ? t('Ride') : t('Drive')
    const pickupId = leg.from_point.micromobility_id
    const dropoffId = leg.to_point.micromobility_id

    return (
        <>
            <Stack direction="row" alignItems="center" gap={1}>
                {icon}
                <Typography variant="body1" fontWeight={600}>{verb}</Typography>
                <Typography variant="body2" color="text.secondary">{`${distStr} · ${formatDuration(leg.end_time - leg.start_time)}`}</Typography>
            </Stack>

            {leg.is_rental && pickupId && (
                <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography variant="caption" color="text.secondary">{t('Pick up at')}</Typography>
                    <Link onClick={() => navigate({ to: '/micromobility/$stopId', params: { stopId: pickupId } })} sx={{ cursor: 'pointer', fontSize: 'body2.fontSize' }}>
                        {leg.from_point.name}
                    </Link>
                </Stack>
            )}

            <Stack direction="row" alignItems="center" gap={0.5}>
                <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                {leg.is_rental && dropoffId
                    ? (
                            <Link onClick={() => navigate({ to: '/micromobility/$stopId', params: { stopId: dropoffId } })} sx={{ cursor: 'pointer', fontSize: 'body2.fontSize' }}>
                                {leg.to_point.name}
                            </Link>
                        )
                    : <Typography variant="body2" color="text.secondary" noWrap>{leg.to_point.name}</Typography>}
            </Stack>
        </>
    )
}

function ArrivedCard({ leg }: { leg: Leg }) {
    const { t } = useTranslation()
    return (
        <>
            <Stack direction="row" alignItems="center" gap={1}>
                <CheckCircleIcon sx={{ color: 'success.main' }} />
                <Typography variant="h6" fontWeight={700}>{t('You\'ve arrived')}</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">{leg.to_point.name}</Typography>
        </>
    )
}

function NextStepPreview({ nextLeg }: { nextLeg: Leg }) {
    const { t } = useTranslation()
    const icon = getIconForMode(nextLeg)
    const isNextTransit = TRANSIT_MODES.includes(nextLeg.mode)
    const duration = formatDuration(nextLeg.end_time - nextLeg.start_time)
    const verb = nextLeg.mode === 'WALK' ? t('Walk') : nextLeg.mode === 'BICYCLE' ? t('Cycle') : t('Ride')

    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{
                px: 1.5, py: 1, borderRadius: 1,
                bgcolor: 'action.hover',
            }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {t('Next:')}
            </Typography>
            <Box sx={{ 'display': 'flex', 'alignItems': 'center', '& .MuiSvgIcon-root': { fontSize: 16 } }}>{icon}</Box>
            <Typography variant="caption" noWrap>
                {isNextTransit
                    ? `${nextLeg.trip?.short_name ?? ''} → ${nextLeg.trip?.headsign ?? nextLeg.to_point.name}`
                    : `${verb} · ${duration}`}
            </Typography>
        </Stack>
    )
}
